import type { OrderRow } from '../model/order-schema';
import { FINISHED_STAGES, OrderStage, type OrderAccent } from '../model/stages';

/* eslint-disable @typescript-eslint/prefer-nullish-coalescing --
   Ported verbatim from cpanel `features/orders/model/helpers.tsx` and
   `tableHeaderSelector.tsx`; the `||` fallbacks are intentional falsy-collapse
   (`service_id: 0`, empty strings). */

/**
 * Distance (metres) at or above which each route leg counts as "long" and gets
 * the critical style. Dead-head distance is unpaid travel, so it turns critical
 * earlier than the revenue-earning order leg (cpanel `LONG_DISTANCE_THRESHOLDS_M`).
 */
export const LONG_DISTANCE_THRESHOLDS_M = {
  /** Courier → origin. */
  deadHead: 5000,
  /** Origin → destination. */
  order: 10000,
} as const;

export interface LongDistanceFlags {
  deadHead: boolean;
  order: boolean;
  hasLongDistance: boolean;
}

/**
 * Flags the route legs that reached their critical distance. Inclusive (>=);
 * missing / non-numeric distances coerce to NaN and are never flagged.
 */
export function getLongDistanceFlags(
  row: Pick<OrderRow, 'dead_head_distance' | 'order_distance'> | null | undefined,
): LongDistanceFlags {
  const deadHead = Number(row?.dead_head_distance) >= LONG_DISTANCE_THRESHOLDS_M.deadHead;
  const order = Number(row?.order_distance) >= LONG_DISTANCE_THRESHOLDS_M.order;
  return { deadHead, order, hasLongDistance: deadHead || order };
}

/**
 * Row tint, cpanel `getOrderRowStyle`: the long-distance warning wins; else a
 * per-service background for "custom" rows (`service_id !== 0` or the fridge
 * vendor). Services 1/3 and unknown ids paint nothing.
 */
export type OrderRowTint = 'long-distance' | 'pharma' | 'shop' | 'fridge' | null;

const SERVICE_TINT: Record<string, OrderRowTint> = {
  '1': null, // Gullar
  '2': 'pharma', // Dorixona
  '3': null, // Sovg'alar
  '23': 'shop', // Do'kon
  fridge: 'fridge',
};

export function getOrderRowTint(
  row: Pick<
    OrderRow,
    'service_id' | 'vendor_order_id' | 'dead_head_distance' | 'order_distance'
  >,
): OrderRowTint {
  if (getLongDistanceFlags(row).hasLongDistance) return 'long-distance';
  const isFridge = row.vendor_order_id === 'xolodilnik';
  const isCustom = row.service_id !== 0 || isFridge;
  if (!isCustom) return null;
  const key = isFridge ? 'fridge' : String(row.service_id || 23);
  return SERVICE_TINT[key] ?? null;
}

/**
 * Total elapsed-since-search badge: counts up from `search_started_at` while
 * the order is live, and disappears (returns null) once the order reaches a
 * terminal stage — never freezes on a stale value (cpanel
 * `getSearchElapsedProps`).
 */
export function getSearchElapsedStart(
  row: Pick<OrderRow, 'search_started_at' | 'search_stopped_at' | 'stage'>,
): string | null {
  if (!row.search_started_at) return null;
  if ((FINISHED_STAGES as readonly number[]).includes(row.stage)) return null;
  // Terminal cancel/failure stages are not all in FINISHED_STAGES (21, 25–28).
  if (row.search_stopped_at && row.stage >= OrderStage.DELIVERED_FINISH) return null;
  return row.search_started_at;
}

/** The ID-cell flag badges (cpanel `IsBadges`: Б / Ф / И). */
export type OrderFlagBadge = 'business' | 'market' | 'integration';

export function getOrderFlagBadges(
  row: Pick<OrderRow, 'is_business' | 'is_market' | 'is_integration'>,
): OrderFlagBadge[] {
  const badges: OrderFlagBadge[] = [];
  if (row.is_business) badges.push('business');
  if (row.is_market) badges.push('market');
  if (row.is_integration) badges.push('integration');
  return badges;
}

/**
 * Courier payment status ids the product-payment column names (cpanel
 * `orderCourierPaymentText`; 0–8). Resolved to `orders:courierPaymentText.<id>`.
 */
export const COURIER_PAYMENT_TEXT_IDS = [0, 1, 2, 3, 4, 5, 6, 7, 8] as const;

/**
 * Product-payment cell, cpanel `ProductPaymentStatusCell`: no product price or
 * a paid product reads "Оплачено"; otherwise the courier payment status text
 * (empty for an unknown status).
 */
export function productPaymentStatusKey(
  row: Pick<OrderRow, 'total_product_price' | 'product_paid' | 'courier_payment_status'>,
): string | null {
  if (!row.total_product_price || row.product_paid) return 'cell.productPaid';
  const status = row.courier_payment_status;
  return status != null &&
    (COURIER_PAYMENT_TEXT_IDS as readonly number[]).includes(status)
    ? `courierPaymentText.${status}`
    : null;
}

/** Debit-timer statuses (cpanel `ProductPaymentStatusTimeCell`): 6 = Долг, 7 = Долг погашен. */
export const COURIER_PAYMENT_DEBT = 6;
export const COURIER_PAYMENT_DEBT_REPAID = 7;

/**
 * Accent hue per timer-status group — the same groups as `timerTooltipKey`,
 * so every stage that shares a label also shares one colour (cpanel
 * `getStageGroupBadge` colours mapped onto design-token hues).
 */
export function stageGroupAccent(stage: number): OrderAccent | null {
  if (stage >= 5 && stage <= 6) return 'pending';
  if (stage === 7) return 'active';
  if (stage === 8) return 'sky';
  if (stage >= 9 && stage <= 10) return 'info';
  if (stage >= 11 && stage <= 14) return 'violet';
  if (stage >= 16 && stage <= 19) return 'danger';
  return null;
}

/**
 * Display label key for a stage in the status-priority list (cpanel
 * `getStageDisplayLabel || name`): the active delivery range reads as its
 * timer-group phrase; anything else falls back to the stage name.
 */
export function stageDisplayLabelKey(stage: number): string | null {
  if (stage >= 5 && stage <= 6) return 'timer.searching';
  if (stage === 7) return 'timer.heading_to_sender';
  if (stage === 8) return 'timer.waiting_order';
  if (stage >= 9 && stage <= 10) return 'timer.heading_to_recipient';
  if (stage >= 11 && stage <= 14) return 'timer.waiting_recipient';
  return null;
}

/** Address cleanup used by the route cell (cpanel `RouteCell`). */
export function cleanOrderAddress(address: string | null | undefined): string {
  return (address ?? '').replaceAll('Ташкент,', '').replaceAll('район', 'р ');
}
